/* eslint-disable react/require-default-props */
import React from "react";

import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link } from "react-router-dom";

import { SidebarListProps } from "../../types";
import { useUserState } from "../../../../../store";
import { BRAND_COLORS } from "../../../../../constants";

const DARK_ACTIVE_BG = "rgba(255, 255, 255, 0.1)";
const DARK_HOVER_BG = "rgba(255, 255, 255, 0.05)";
const DARK_TEXT = "rgba(255, 255, 255, 0.88)";
const DARK_TEXT_MUTED = "rgba(255, 255, 255, 0.35)";
const DARK_ACTIVE_INDICATOR = BRAND_COLORS.primary;

interface Props {
  sidebarList: SidebarListProps[];
  open: boolean;
  mobile: boolean;
  /** Dark sidebar variant (light text on dark background) */
  dark?: boolean;
}

function getItemSx(
  item: SidebarListProps,
  open: boolean,
  mobile: boolean,
  dark: boolean,
  primaryMain: string,
  textSecondary: string,
  grey100: string,
) {
  if (dark) {
    if (open) {
      return {
        py: mobile ? 1 : 1.25,
        px: 1.5,
        mb: 0.5,
        borderRadius: "8px",
        backgroundColor: item.isSelected ? DARK_ACTIVE_BG : "transparent",
        color: item.isSelected ? "#ffffff" : DARK_TEXT,
        borderLeft: item.isSelected
          ? `3px solid ${DARK_ACTIVE_INDICATOR}`
          : "3px solid transparent",
        "&:hover": {
          backgroundColor: item.isSelected ? DARK_ACTIVE_BG : DARK_HOVER_BG,
          color: "#ffffff",
        },
        "&.Mui-disabled": { opacity: 0.4, color: DARK_TEXT_MUTED },
        transition: "all 0.15s ease",
        justifyContent: "flex-start",
        minHeight: 44,
      };
    }
    return {
      p: 0,
      mb: 0.5,
      width: 44,
      height: 44,
      minWidth: 44,
      minHeight: 44,
      borderRadius: "10px",
      mx: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: item.isSelected ? DARK_ACTIVE_BG : "transparent",
      color: item.isSelected ? "#ffffff" : DARK_TEXT,
      "&:hover": {
        backgroundColor: item.isSelected ? DARK_ACTIVE_BG : DARK_HOVER_BG,
        color: "#ffffff",
      },
      "&.Mui-disabled": { opacity: 0.4, color: DARK_TEXT_MUTED },
      transition: "all 0.15s ease",
    };
  }

  if (open) {
    return {
      py: mobile ? 1 : 1.1,
      px: 1.5,
      mb: 0.5,
      borderRadius: "8px",
      backgroundColor: item.isSelected ? `${primaryMain}12` : "transparent",
      color: item.isSelected ? primaryMain : textSecondary,
      fontWeight: item.isSelected ? 600 : 400,
      "&:hover": {
        backgroundColor: item.isSelected ? `${primaryMain}18` : grey100,
        color: item.isSelected ? primaryMain : "text.primary",
      },
      "&.Mui-disabled": { opacity: 0.45, color: textSecondary },
      transition: "all 0.15s ease",
      justifyContent: "flex-start",
      minHeight: 44,
    };
  }

  return {
    p: 0,
    mb: 0.5,
    width: 44,
    height: 44,
    minWidth: 44,
    minHeight: 44,
    borderRadius: "10px",
    mx: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: item.isSelected ? `${primaryMain}12` : "transparent",
    color: item.isSelected ? primaryMain : textSecondary,
    "&:hover": {
      backgroundColor: item.isSelected ? `${primaryMain}18` : grey100,
      color: item.isSelected ? primaryMain : "text.primary",
    },
    "&.Mui-disabled": { opacity: 0.45, color: textSecondary },
    transition: "all 0.15s ease",
  };
}

export default function SidebarList({
  sidebarList,
  open,
  mobile,
  dark = false,
}: Props) {
  const theme = useTheme();
  const { user } = useUserState();
  const primaryMain = theme.palette.primary.main;
  const textSecondary = theme.palette.text.secondary;
  const grey100 = theme.palette.grey[100];

  return (
    <List
      component="nav"
      sx={{
        py: mobile ? 0.5 : 1,
        px: open ? 1.5 : 0,
        display: "flex",
        flexDirection: "column",
        alignItems: open ? "stretch" : "center",
      }}
    >
      <>
        {sidebarList
          .filter((item) =>
            item.permissions?.some((permission) =>
              user?.roles?.includes(permission),
            ),
          )
          .map((item) => {
            const linkProps = item.disabled
              ? {}
              : { component: Link, to: item.link };
            const content = (
              <ListItemButton
                {...linkProps}
                disabled={item.disabled}
                aria-current={item.isSelected ? "page" : undefined}
                sx={getItemSx(
                  item,
                  open,
                  mobile,
                  dark,
                  primaryMain,
                  textSecondary,
                  grey100,
                )}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "auto",
                    mr: open ? 1.5 : 0,
                    color: "inherit",
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                      color: "inherit",
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {open && (
                  <ListItemText
                    primaryTypographyProps={{
                      fontSize: mobile ? 13 : 14,
                      fontWeight: item.isSelected ? 600 : 400,
                      letterSpacing: 0.1,
                      color: "inherit",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    primary={item.name}
                  />
                )}
                {open && item.disabled && (
                  <Chip
                    label="Soon"
                    size="small"
                    sx={{
                      height: 18,
                      ml: 1,
                      fontSize: "9px",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: dark ? DARK_TEXT : primaryMain,
                      backgroundColor: dark
                        ? "rgba(255,255,255,0.12)"
                        : `${primaryMain}14`,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                )}
              </ListItemButton>
            );
            return (
              <Box key={item.name}>
                {open ? (
                  content
                ) : (
                  <Tooltip
                    title={
                      item.disabled ? `${item.name} · Coming soon` : item.name
                    }
                    placement="right"
                    arrow
                  >
                    {content}
                  </Tooltip>
                )}
                {item.subMenu &&
                  item.subMenu.some(
                    (subMenu: SidebarListProps) => subMenu.isSelected,
                  ) && (
                    <List sx={{ pl: open ? 2 : 0 }}>
                      {item.subMenu.map((subItem: SidebarListProps) => {
                        const subContent = (
                          <ListItemButton
                            component={Link}
                            to={subItem.link}
                            key={subItem.name}
                            aria-current={
                              subItem.isSelected ? "page" : undefined
                            }
                            sx={
                              dark
                                ? {
                                    py: mobile ? 0.75 : 1,
                                    px: open ? 1.5 : 1,
                                    mb: 0.25,
                                    borderRadius: "8px",
                                    backgroundColor: subItem.isSelected
                                      ? DARK_ACTIVE_BG
                                      : "transparent",
                                    color: subItem.isSelected
                                      ? "#ffffff"
                                      : DARK_TEXT,
                                    borderLeft: subItem.isSelected
                                      ? `3px solid ${DARK_ACTIVE_INDICATOR}`
                                      : "3px solid transparent",
                                    "&:hover": {
                                      backgroundColor: subItem.isSelected
                                        ? DARK_ACTIVE_BG
                                        : DARK_HOVER_BG,
                                      color: "#ffffff",
                                    },
                                    transition: "all 0.15s ease",
                                    justifyContent: open
                                      ? "flex-start"
                                      : "center",
                                    minHeight: 38,
                                  }
                                : {
                                    py: mobile ? 0.75 : 1,
                                    px: 1.5,
                                    mb: 0.25,
                                    borderRadius: "8px",
                                    backgroundColor: subItem.isSelected
                                      ? `${primaryMain}12`
                                      : "transparent",
                                    color: subItem.isSelected
                                      ? primaryMain
                                      : textSecondary,
                                    "&:hover": {
                                      backgroundColor: subItem.isSelected
                                        ? `${primaryMain}18`
                                        : grey100,
                                      color: subItem.isSelected
                                        ? primaryMain
                                        : theme.palette.text.primary,
                                    },
                                    transition: "all 0.15s ease",
                                    justifyContent: open
                                      ? "flex-start"
                                      : "center",
                                    minHeight: 38,
                                  }
                            }
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: "auto",
                                mr: open ? 1.5 : 0,
                                color: "inherit",
                                justifyContent: "center",
                                display: "flex",
                                alignItems: "center",
                                flexShrink: 0,
                                "& .MuiSvgIcon-root": {
                                  fontSize: 18,
                                  color: "inherit",
                                },
                              }}
                            >
                              {subItem.icon}
                            </ListItemIcon>
                            {open && (
                              <ListItemText
                                primaryTypographyProps={{
                                  fontSize: mobile ? 12 : 13,
                                  fontWeight: subItem.isSelected ? 600 : 400,
                                  letterSpacing: 0.1,
                                  color: "inherit",
                                }}
                                primary={subItem.name}
                              />
                            )}
                          </ListItemButton>
                        );
                        return open ? (
                          subContent
                        ) : (
                          <Tooltip
                            title={subItem.name}
                            placement="right"
                            arrow
                            key={subItem.name}
                          >
                            {subContent}
                          </Tooltip>
                        );
                      })}
                    </List>
                  )}
              </Box>
            );
          })}
      </>
    </List>
  );
}
